import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { topRightStack } from '../utils/toastStack';
import './systemHealthToast.css';

const TOAST_MS = 15000;
const TONE = '#f59e0b';

/**
 * Soft, low "dum-dum" alert: two sine notes (G3 → D3) with a gentle attack and a slow fade, through
 * a low-pass filter. A quiet 2nd harmonic keeps it audible on laptop speakers that can't play bass.
 * One AudioContext is reused — browsers cap how many can exist, and a new one per alert stops working.
 */
let audioCtx = null;
function playSoftAlert() {
    try {
        audioCtx = audioCtx || new (window.AudioContext || window.webkitAudioContext)();
        if (audioCtx.state === 'suspended') audioCtx.resume();
        const ctx = audioCtx;
        const out = ctx.createBiquadFilter();
        out.type = 'lowpass';
        out.frequency.value = 900;
        out.connect(ctx.destination);
        const note = (freq, start, dur, level) => {
            const t0 = ctx.currentTime + start;
            [[freq, level], [freq * 2, level * 0.28]].forEach(([f, lvl]) => {
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.type = 'sine';
                osc.frequency.setValueAtTime(f, t0);
                gain.gain.setValueAtTime(0.0001, t0);
                gain.gain.exponentialRampToValueAtTime(lvl, t0 + 0.025);   // soft attack (no click)
                gain.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);   // slow fade
                osc.connect(gain);
                gain.connect(out);
                osc.start(t0);
                osc.stop(t0 + dur + 0.05);
            });
        };
        note(196, 0.00, 0.55, 0.22);  // G3
        note(147, 0.30, 0.85, 0.24);  // D3
    } catch (_) { /* no audio available */ }
}

function WrenchIcon() {
    return (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M14.5 5.5a4 4 0 00-5 5L4 16l4 4 5.5-5.5a4 4 0 005-5l-2.5 2.5-3-.5-.5-3z" />
        </svg>
    );
}

/**
 * TechPauseToast — listens for the global 'techPause' CustomEvent
 * dispatched by WS message handlers in Matches.js / MatchDetail.js.
 * No separate WS connection needed — uses window custom events.
 */
export default function TechPauseToast() {
    const { t } = useTranslation();
    const [toasts, setToasts] = useState([]);

    const dismiss = useCallback((id) => setToasts(prev => prev.filter(x => x.id !== id)), []);

    useEffect(() => {
        const handler = (e) => {
            const data = e.detail || {};
            const id = `${Date.now()}:${Math.random()}`;
            const ts = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            setToasts(prev => [...prev, { id, ...data, ts }].slice(-4));
            playSoftAlert();
        };
        window.addEventListener('techPause', handler);
        return () => window.removeEventListener('techPause', handler);
    }, []);

    if (!toasts.length) return null;

    return createPortal(
        <>
            {toasts.map(toast => (
                <div key={toast.id} className="sh-toast" style={{ '--sh-tone': TONE }}>
                    <span className="sh-ic pulse"><WrenchIcon /></span>
                    <div className="sh-main">
                        <div className="sh-top">
                            <span className="sh-title">{t('techToast.title', 'Tech pause')}</span>
                            {toast.team_side && <span className="sh-value">{toast.team_side}</span>}
                            <span className="sh-time">{toast.ts}</span>
                        </div>
                        <div className="sh-msg" style={{ whiteSpace: 'normal' }}>
                            <b>{toast.player_name || t('techToast.aPlayer', 'A player')}</b> {t('techToast.called', 'called a technical pause')}
                        </div>
                        {toast.match_id && (
                            <div className="sh-foot">
                                <span>{t('techToast.match', 'Match #{{id}}', { id: toast.match_id })}</span>
                                {toast.match_link && (
                                    <a className="sh-link" href={toast.match_link} onClick={() => dismiss(toast.id)}>{t('techToast.view', 'View match')} →</a>
                                )}
                            </div>
                        )}
                    </div>
                    <button type="button" className="sh-x" aria-label={t('common.close', 'Close')} onClick={() => dismiss(toast.id)}>×</button>
                    <span className="sh-bar" style={{ animationDuration: `${TOAST_MS}ms` }} onAnimationEnd={() => dismiss(toast.id)} />
                </div>
            ))}
        </>,
        topRightStack()
    );
}
